import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './contexts/AuthContext';
import { logOut } from './firebase';
import Login from './components/auth/Login';
import { userService } from './services/userService';

// Components
import Navbar from './components/layout/Navbar';
import Watchlist from './components/watchlist/Watchlist';
import Dashboard from './components/dashboard/Dashboard';
import PositionsPanel from './components/positions/PositionsPanel';
import ClosePositionModal from './components/positions/ClosePositionModal';
import OpenPositionModal from './components/positions/OpenPositionModal';
import AllOrdersPage from './components/pages/AllOrdersPage';
import ChatPage from './components/pages/ChatPage';
import AdminPage from './components/pages/AdminPage';
import MobileNav from './components/layout/MobileNav';
import CollapsiblePanel from './components/ui/CollapsiblePanel';
import { Eye, Briefcase } from 'lucide-react';
import { AgentsPage } from './components/agents';

// Types
import { Position, ClosedTrade } from './types/position';
import { Quote, UserProfile, AgentTrade, Recommendation } from './types';

// API Service
import { apiService } from './services/apiService';
import { etToday } from './utils/etDate';
import { formatDateTime } from './utils/format';

// Lucide Icons
import { Loader2, AlertCircle, X } from 'lucide-react';

// Page types
type PageType = 'dashboard' | 'all-orders' | 'agents' | 'chat' | 'admin';

// Build version - update on each deploy
const BUILD_VERSION = 'v0.1.8';
// B-026: ET and human-readable like every other time shown (was a raw UTC ISO string)
const BUILD_TS = formatDateTime(process.env.REACT_APP_BUILD_TS || new Date().toISOString());

function App() {
  const { currentUser } = useAuth();

  // State
  const [currentPage, setCurrentPage] = useState<PageType>('dashboard');
  const [orderFilter, setOrderFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [agentTrades, setAgentTrades] = useState<AgentTrade[]>([]);
  const [watchlistQuotes, setWatchlistQuotes] = useState<{[key: string]: Quote}>({});
  const [error, setError] = useState<string | null>(null);
  const [addingSymbol, setAddingSymbol] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [isOrchestrating, setIsOrchestrating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Agent leaderboard stats (for navbar)
  const [agentStats, setAgentStats] = useState<{count: number; totalPnl: number; avgWinRate: number; closedCount: number}>({count: 0, totalPnl: 0, avgWinRate: 0, closedCount: 0});

  // Positions - loaded from API
  const [openPositions, setOpenPositions] = useState<Position[]>([]);
  const [closedTrades, setClosedTrades] = useState<ClosedTrade[]>([]);
  
  // Modal state
  const [positionToClose, setPositionToClose] = useState<Position | null>(null);
  const [tradeToTake, setTradeToTake] = useState<{ trade: AgentTrade; recommendation: Recommendation } | null>(null);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        let profile: UserProfile;
        try {
          profile = await userService.getUserProfile();
        } catch {
          const defaultProfile: UserProfile = {
            email: currentUser.email || '',
            name: currentUser.displayName || 'User',
            watchlist: ['AAPL', 'MSFT', 'NFLX', 'UBER', 'PLTR', 'HOOD', 'ORCL'],
          };
          profile = defaultProfile;
          try {
            await userService.createUserProfile(defaultProfile as unknown as Record<string, unknown>);
          } catch (createErr) {
            console.warn('Could not save new profile, using defaults:', createErr);
          }
        }
        
        // Ensure watchlist always has values
        if (!profile.watchlist?.length) {
          profile.watchlist = ['AAPL', 'MSFT', 'NFLX', 'UBER', 'PLTR', 'HOOD', 'ORCL'];
        }
        
        setUserProfile(profile);

        const [trades, quotes, openPos, closedPos, leaderboard, agentClosedTrades] = await Promise.all([
          apiService.getAgentTrades('open').catch(() => [] as AgentTrade[]),
          userService.getMarketQuotes(profile.watchlist).catch(() => ({ quotes: {} as Record<string, Quote> })),
          userService.getPositions('open').catch(() => []),
          userService.getPositions('closed').catch(() => []),
          apiService.getAgentLeaderboard(20).catch(() => []),
          apiService.getAgentTrades('closed').catch(() => [] as AgentTrade[]),
        ]);
        
        setOpenPositions(openPos || []);
        setWatchlistQuotes(quotes?.quotes || {});
        setAgentTrades(trades || []);
        
        // Set agent stats for navbar — use actual closed trade count, not stale performance docs
        const lb: any[] = leaderboard || [];
        const closedTradesList = agentClosedTrades || [];
        const actualClosedCount = closedTradesList.length;
        if (lb.length > 0) {
          const actualWins = closedTradesList.filter((t: any) => (t.position?.realized_pnl ?? 0) >= 0).length;
          setAgentStats({
            count: lb.length,
            totalPnl: lb.reduce((s: number, a: any) => s + a.performance.total_pnl, 0),
            avgWinRate: actualClosedCount > 0 ? (actualWins / actualClosedCount) * 100 : 0,
            closedCount: actualClosedCount,
          });
        }
        
        const closedTradesFromPositions = (closedPos || []).map((pos: Position) => ({
          id: pos.id,
          symbol: pos.symbol,
          strategy: pos.strategy,
          strike: pos.strike,
          premium: pos.entry_premium,
          pnl: pos.realized_pnl || 0,
          pnlPercent: pos.realized_roi_pct ?? pos.realized_pnl_pct ?? 0,
          closeDate: pos.exit_date || '',
          openDate: pos.entry_date || '',
          outcome: (pos.realized_pnl || 0) > 0 ? 'profit' : (pos.realized_pnl || 0) < 0 ? 'loss' : 'breakeven',
        } as ClosedTrade));
        setClosedTrades(closedTradesFromPositions);

      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser]);


  const handleRunOrchestration = useCallback(async () => {
    setIsOrchestrating(true);
    try {
      await apiService.runOrchestration();
      // Cycle triggered in background — data will update on next page load
    } catch (err) {
      setError('Failed to trigger orchestration');
    } finally {
      setIsOrchestrating(false);
    }
  }, []);

  const handleRefreshPositions = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await apiService.refreshPositions();
      // Re-fetch data so UI reflects updated prices
      const [trades, leaderboard, closedTr] = await Promise.all([
        apiService.getAgentTrades('open'),
        apiService.getAgentLeaderboard(20),
        apiService.getAgentTrades('closed').catch(() => [] as AgentTrade[]),
      ]);
      setAgentTrades(trades || []);
      const lb: any[] = leaderboard || [];
      const closedTradesList2 = closedTr || [];
      const actualClosedCount = closedTradesList2.length;
      if (lb.length > 0) {
        const actualWins = closedTradesList2.filter((t: any) => (t.position?.realized_pnl ?? 0) >= 0).length;
        setAgentStats({
          count: lb.length,
          totalPnl: lb.reduce((s: number, a: any) => s + a.performance.total_pnl, 0),
          avgWinRate: actualClosedCount > 0 ? (actualWins / actualClosedCount) * 100 : 0,
          closedCount: actualClosedCount,
        });
      }
    } catch (err) {
      setError('Failed to refresh positions');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const handleTakePosition = useCallback((trade: AgentTrade) => {
    const rec: Recommendation = {
      symbol: trade.position.symbol,
      strategy: trade.position.strategy,
      strike: trade.position.strike,
      expiration: trade.position.expiration || '',
      premium: trade.position.entry_price,
      confidence: trade.position.entry_ta_score || 0,
      rationale: '',
      option_symbol: trade.position.option_symbol,
      price_at_rec: trade.position.stock_entry_price || trade.position.entry_price,
      stock_entry_price: trade.position.stock_entry_price || undefined,
      current_stock_price: trade.position.current_stock_price || undefined,
      cost_basis: trade.position.cost_basis || undefined,
    };
    setTradeToTake({ trade, recommendation: rec });
  }, []);

  const handleConfirmOpenPosition = useCallback(async (data: {
    recommendation_id: string;
    symbol: string;
    strategy: string;
    strike: number;
    expiration: string;
    option_symbol: string;
    contracts: number;
    entry_premium: number;
    price_at_entry: number;
    agent_position_id?: string;
    stock_entry_price?: number;
    current_stock_price?: number;
    cost_basis?: number;
  }) => {
    try {
      await userService.createPosition(data);
      setTradeToTake(null);
      const openPos = await userService.getPositions('open');
      setOpenPositions(openPos || []);
    } catch (err) {
      setError('Failed to open position');
    }
  }, []);

  const handleAddSymbol = useCallback(async (symbol: string) => {
    if (!symbol.trim() || !userProfile) return;
    
    const upperSymbol = symbol.toUpperCase().trim();
    if (userProfile.watchlist.includes(upperSymbol)) return;

    setAddingSymbol(true);
    try {
      const result = await userService.addToWatchlist(upperSymbol);
      if (result.success) {
        setUserProfile({ ...userProfile, watchlist: result.watchlist });
        const quotes = await userService.getMarketQuotes(result.watchlist);
        setWatchlistQuotes(quotes?.quotes || {});
      }
    } catch (err) {
      console.error('Error adding symbol:', err);
      setError(`Failed to add ${upperSymbol}`);
    } finally {
      setAddingSymbol(false);
    }
  }, [userProfile]);

  const handleRemoveSymbol = useCallback(async (symbol: string) => {
    if (!userProfile) return;
    
    try {
      const result = await userService.removeFromWatchlist(symbol);
      if (result.success) {
        setUserProfile({ ...userProfile, watchlist: result.watchlist });
        const newQuotes = { ...watchlistQuotes };
        delete newQuotes[symbol];
        setWatchlistQuotes(newQuotes);
      }
    } catch (err) {
      console.error('Error removing symbol:', err);
    }
  }, [userProfile, watchlistQuotes]);

  const handleSelectSymbol = useCallback((symbol: string) => {
    setSelectedSymbol(prev => prev === symbol ? null : symbol);
  }, []);

  const handleViewPosition = useCallback((_position: Position) => {
    // Position detail modal - future feature
  }, []);

  const handleClosePosition = useCallback((position: Position) => {
    const pos = (position as any).position ? (position as any).position : position;
    setPositionToClose(pos);
  }, []);

  const handleConfirmClose = useCallback(async (positionId: string, exitPremium: number, exitStockPrice?: number) => {
    const result = await userService.closePosition(positionId, exitPremium, exitStockPrice);
    
    if (!result.success) {
      throw new Error('Failed to close position');
    }
    
    const pos = openPositions.find(p => p.id === positionId);
    if (!pos) return;
    
    const closedTrade: ClosedTrade = {
      id: pos.id,
      symbol: pos.symbol,
      strategy: pos.strategy,
      strike: pos.strike,
      premium: pos.entry_premium,
      pnl: result.realized_pnl || 0,
      pnlPercent: result.realized_roi_pct ?? result.realized_pnl_pct ?? 0,
      closeDate: etToday(),              // B-026: the ET date, not UTC (next day after 8 PM ET)
      openDate: pos.entry_date || '',
      outcome: (result.realized_pnl || 0) > 0 ? 'profit' : (result.realized_pnl || 0) < 0 ? 'loss' : 'breakeven',
    };
    
    setOpenPositions(prev => prev.filter(p => p.id !== positionId));
    setClosedTrades(prev => [closedTrade, ...prev]);
  }, [openPositions]);

  if (!currentUser) {
    return <Login />;
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center">
        <img 
          src="/logo.png" 
          alt="Quantify" 
          className="w-16 h-16 rounded-xl mb-4"
        />
        <Loader2 className="w-8 h-8 animate-spin text-green-400 mb-3" />
        <p className="text-slate-400 text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] pb-16 md:pb-0">
      <Navbar
        userName={currentUser.displayName || ''}
        userEmail={currentUser.email || ''}
        userPhoto={currentUser.photoURL}
        onLogout={logOut}
        onNavigate={(page) => {
          if (page.startsWith('all-orders:')) {
            const filter = page.split(':')[1] as 'all' | 'open' | 'closed';
            setOrderFilter(filter);
            setCurrentPage('all-orders');
          } else {
            if (page === 'all-orders') setOrderFilter('all');
            setCurrentPage(page as PageType);
          }
        }}
        onRunOrchestration={handleRunOrchestration}
        isOrchestrating={isOrchestrating}
        onRefreshPositions={handleRefreshPositions}
        isRefreshing={isRefreshing}
        agentCount={agentStats.count}
        avgWinRate={agentStats.avgWinRate}
        openPositionCount={agentTrades.length}
        closedPositionCount={agentStats.closedCount}
        isAdmin={userProfile?.role === 'admin'}
        currentPage={currentPage}
        newsletterEnabled={userProfile?.preferences?.newsletter_enabled !== false}
        onToggleNewsletter={async (enabled) => {
          try {
            await apiService.toggleNewsletter(enabled);
            setUserProfile(prev => prev ? {
              ...prev,
              preferences: { ...prev.preferences, newsletter_enabled: enabled }
            } : prev);
          } catch (err) {
            console.error('Failed to toggle newsletter:', err);
          }
        }}
      />

      {currentPage === 'all-orders' && (
        <AllOrdersPage
          onBack={() => setCurrentPage('dashboard')}
          initialFilter={orderFilter}
        />
      )}

      {currentPage === 'agents' && (
        <AgentsPage onBack={() => setCurrentPage('dashboard')} />
      )}

      {currentPage === 'chat' && (
        <ChatPage onBack={() => setCurrentPage('dashboard')} />
      )}

      {currentPage === 'admin' && (
        <AdminPage onBack={() => setCurrentPage('dashboard')} />
      )}

      {currentPage === 'dashboard' && (
        <>
          {error && (
            <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-right duration-300">
              <div className="w-80 p-4 bg-gradient-to-br from-slate-800 to-slate-900 border border-red-500/30 rounded-xl shadow-2xl shadow-red-500/10 backdrop-blur-xl">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white mb-0.5">Error</p>
                    <p className="text-xs text-slate-400 leading-relaxed">{error}</p>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="p-1 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 h-0.5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 to-red-400 animate-[shrink_5s_linear_forwards]" style={{width: '100%'}} />
                </div>
              </div>
            </div>
          )}

          <main className="p-4 pb-14">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:h-[calc(100dvh-120px)]">
              
              {/* 7.3 (owner feedback): on mobile the side rails collapse and the desk
                  panel is ordered FIRST, so the phone opens straight onto the main view
                  instead of the watchlist. Desktop order/layout is unchanged. */}
              <CollapsiblePanel
                title="Watchlist"
                icon={Eye}
                count={userProfile?.watchlist?.length}
                className="md:col-span-2 order-2 md:order-1"
              >
                <Watchlist
                  symbols={userProfile?.watchlist || []}
                  quotes={watchlistQuotes}
                  onAddSymbol={handleAddSymbol}
                  onRemoveSymbol={handleRemoveSymbol}
                  onSelectSymbol={handleSelectSymbol}
                  selectedSymbol={selectedSymbol}
                  isAdding={addingSymbol}
                />
              </CollapsiblePanel>

              <div className="md:col-span-8 flex flex-col overflow-hidden order-1 md:order-2">
                <Dashboard
                  agentTrades={agentTrades}
                  onTakePosition={handleTakePosition}
                  onNavigateToChat={() => setCurrentPage('chat')}
                />
              </div>

              <CollapsiblePanel
                title="Open Positions"
                icon={Briefcase}
                count={openPositions.length}
                className="md:col-span-2 order-3 md:order-3"
              >
                <PositionsPanel
                  openPositions={openPositions}
                  closedTrades={closedTrades}
                  onViewPosition={handleViewPosition}
                  onClosePosition={handleClosePosition}
                />
              </CollapsiblePanel>
            </div>
          </main>

          <footer className="fixed bottom-0 left-0 right-0 py-1.5 px-4 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700/50 z-10">
            <div className="flex items-center justify-between">
              <p className="text-10 text-slate-500">
                Creative Sage &bull; <span className="text-yellow-500/70">For educational purposes only. Not financial advice.</span>
              </p>
              <span className="text-9 text-slate-600 font-mono">{BUILD_VERSION} &bull; {BUILD_TS}</span>
            </div>
          </footer>

          {positionToClose && (
            <ClosePositionModal
              position={positionToClose}
              onClose={() => setPositionToClose(null)}
              onConfirm={handleConfirmClose}
            />
          )}

          {tradeToTake && (
            <OpenPositionModal
              recommendation={tradeToTake.recommendation}
              agentName={tradeToTake.trade.agent.name}
              agentPositionId={tradeToTake.trade.position.id}
              onClose={() => setTradeToTake(null)}
              onConfirm={handleConfirmOpenPosition}
            />
          )}
        </>
      )}

      {/* 7.3: mobile-only bottom tab bar. Reuses the SAME navigation semantics as the
          Navbar (including the 'all-orders' filter reset) so both navs stay in sync. */}
      <MobileNav
        currentPage={currentPage}
        onNavigate={(page) => {
          if (page === 'all-orders') setOrderFilter('all');
          setCurrentPage(page as PageType);
        }}
      />
    </div>
  );
}

export default App;
